import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Cloudinary } from '@cloudinary/angular-5.x';
import { FirebaseAuthService } from '../firebase-integration/firebase-auth.service';
import { ProfileModel, ProfileListModel, FollowingModel, TransactionModel, CartModel, UserModel, SalesRepMaster } from '../../models/profile.model';
import { LocationServices } from '../../services/locationServices';
import firebase from 'firebase';
import { environment } from '../../environment/environment';
import { JobberModel, JobberMatchModel } from '../../models/jobber.model';
import { RequiredEmployeeModel } from '../../models/employee.model';

@Injectable()
export class ProfileService {
  backendURL = environment.backendURL;

  constructor(
    public locationServices: LocationServices,
    public cloudinary: Cloudinary,
    public fAuthService: FirebaseAuthService,
    public http: HttpClient
  ) { }

  getData(id: string): Promise<ProfileModel> {
    return new Promise<any>((resolve, reject) => {
      firebase.database().ref('/users/' + id).once('value')
        .then(snapshot => {
          return resolve(snapshot.val());
        })
        .catch(error => {
          this.handleError(error);
        });
    });
  }

  getUser(id: string): Promise<UserModel> {
    return new Promise<UserModel>((resolve, reject) => {
      firebase.database().ref('/users/' + id).once('value')
        .then(snapshot => {
          return resolve(snapshot.val().user);
        })
        .catch(error => {
          this.handleError(error);
        });
    });
  }

  getEmployeeAvailability(uid: string): Promise<JobberModel> {

    return new Promise<JobberModel>((resolve, reject) => {

      firebase.database().ref('employeeAvailability').child(uid).once('value', snapshot => {

        if (snapshot && snapshot.val()) {
          return resolve(snapshot.val());
        } else {
          return reject();
        }
      }, error => {
        return reject();
      });
    });
  }

  saveEmployeeAvailability(jobber: JobberModel, user: UserModel) {

    if (jobber.uid) {
      jobber.modifiedOn = new Date().toDateString();
    } else {
      jobber.createdOn = new Date().toDateString();
      jobber.modifiedOn = jobber.createdOn;

      jobber.uid = user.id;
      jobber.name = user.name;
      jobber.image = user.image;
      jobber.phone = user.phonePrefix.concat('-').concat(user.phone);
      jobber.email = user.email;
    }

    return new Promise((resolve, reject) => {

      firebase.database().ref('employeeAvailability').child(jobber.uid).update(jobber).then(() => {
        return resolve();
      }, error => {
        return reject();
      });
    });
  }

  checkExistingJobberMatch(jobber: JobberModel, company: RequiredEmployeeModel) {
    let jobberMatch = new JobberMatchModel(company, jobber);

    return new Promise((resolve, reject) => {

      firebase.database().ref('contracts/jobberMatches')
        .child(jobberMatch.matchId)
        .once('value', snapshot => {

          if(snapshot && snapshot.val()) {
            return reject();
          }

          return resolve();
        }, error => {
          return reject();
        });
    });
  }

  setJobberMatch(jobber: JobberModel, company: RequiredEmployeeModel) {
    let jobberMatch = new JobberMatchModel(company, jobber);

    return new Promise((resolve, reject) => {

      firebase.database().ref('contracts/jobberMatches')
        .child(jobberMatch.matchId)
        .update(jobberMatch).then(() => {

          return resolve();
        }, error => {
          return reject();
        });
    });
  }

  getJobberMatches(cid: string): Promise<Array<JobberMatchModel>> {

    return new Promise<Array<JobberMatchModel>>((resolve, reject) => {

      firebase.database().ref('contracts/jobberMatches')
        .orderByChild('companyId')
        .equalTo(cid)
        .once('value', snapshot => {

          if (snapshot && snapshot.val())
            return resolve(Object.values(snapshot.val()));

          return reject();
        }, error => {
          return reject();
        });
    });
  }

  getJobberContracts(cid: string): Promise<Array<JobberMatchModel>> {

    return new Promise<Array<JobberMatchModel>>((resolve, reject) => {

      firebase.database().ref('contracts/jobberContracts')
        .orderByChild('companyId')
        .equalTo(cid)
        .once('value', snapshot => {

          if (snapshot && snapshot.val())
            return resolve(Object.values(snapshot.val()));

          return reject();
        }, error => {
          return reject();
        });
    });
  }

  finalizeJobberMatch(jobberMatch: JobberMatchModel) {
    jobberMatch.createdOn = new Date().toDateString();

    return new Promise((resolve, reject) => {

      firebase.database().ref('contracts/jobberContracts')
        .child(jobberMatch.matchId)
        .update(jobberMatch).then(() => {

          firebase.database().ref('contracts/jobberMatches')
            .child(jobberMatch.matchId).remove();

          return resolve();
        }, error => {
          return reject();
        });
    });
  }

  async getLockCode(id): Promise<any> {
    let data = {
      uid: id
    };
    let token;

    await firebase.auth().currentUser.getIdToken(true).then(idToken => {
      token = idToken;
    }).catch(error => {
      return Promise.reject(error);
    });

    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': token
      })
    };

    return new Promise((resolve, reject) => {
      this.http.post((this.backendURL + 'lockCode'), data, httpOptions)
        .subscribe((res) => {

          if (res['status'] == 'success')
            return resolve(res['result']);

          return reject();
        }, error => {
          return reject(error);
        });
    });
  }

  async sendEmailRequest(to: string, subject: string, html: string): Promise<any> {

    let data = {
      to: to,
      subject: subject,
      html: html
    };
    let token;

    await firebase.auth().currentUser.getIdToken(true).then(idToken => {
      token = idToken;
    }).catch(error => {
      return Promise.reject(error);
    });

    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': token
      })
    };

    return new Promise((resolve, reject) => {

      this.http.post((this.backendURL + 'sendMail/doNotReply'), data, httpOptions)
        .subscribe((res) => {

          if (res['status'] == 'success')
            return resolve(res['result']);

          return reject();
        }, error => {
          return reject(error);
        });
    });
  }

  getFollowingData(id): Promise<Array<FollowingModel>> {
    return new Promise<any>((resolve, reject) => {
      firebase.database().ref('/users/' + id)
        .child('following')
        .once('value')
        .then(snapshot => {
          return resolve(snapshot.val());
        })
        .catch(error => {
          this.handleError(error);
        });
    });
  }

  followThis(uid, follow) {
    follow.createdOn = Date.now();

    return new Promise((resolve, reject) => {

      firebase.database().ref('/users/' + uid)
        .child('following')
        .push(follow)
        .then(snap => {
          if (snap && snap.key) {
            return resolve();
          } else {
            return reject();
          }
        })
    });
  }

  checkIfNotFollowing(uid, serachId): Promise<Boolean> {

    return new Promise<Boolean>((resolve, reject) => {

      firebase.database().ref('/users/' + uid + '/following/')
        .orderByChild('id')
        .equalTo(serachId)
        .once('value', snapshot => {

          if (!snapshot.val()) {
            return resolve(true);
          } else {
            return reject(false);
          }
        });
    });
  }

  stopFollowing(uid, cid) {

    return new Promise((resolve, reject) => {

      firebase.database().ref('/users/' + uid)
        .child('following')
        .orderByChild('id')
        .equalTo(cid)
        .once('value', snapshot => {

          if (snapshot.val()) {
            var idToRemove = Object.keys(snapshot.val())[0];

            firebase.database().ref('/users/' + uid)
              .child('following')
              .child(idToRemove)
              .remove();
          }
        });
    });
  }

  async getAllProfilesForList(list, following): Promise<Array<ProfileListModel>> {
    let profileList = new Array<ProfileListModel>();
    let profileDetailsRef = firebase.database().ref('/users/');
    let followingIdList
    if (following) {
      if (typeof (following) == "object") {
        following = Object.values(following);
      }
      followingIdList = following.map(a => a.id);
    } else {
      followingIdList = new Array<any>();
    }

    return new Promise<Array<ProfileListModel>>(async (resolve, reject) => {

      await this.fAuthService.getCurrentUserId().then(async loggedInUserId => {

        for (var i = 0; i < list.length; i++) {
          await profileDetailsRef.child(list[i])
            .once('value', snapshot => {

              if (snapshot.val()) {
                if (loggedInUserId != snapshot.val().user.id)
                  profileList.push(this.mapProfileDetailsToList(snapshot.val(), followingIdList));
              }
            }).catch(error => {
              debugger;
            });
        }

        // list.forEach(async element => {
        //   await profileDetailsRef.child(element)
        //     .once('value', snapshot => {
        //       if (snapshot.val()) {
        //         if (loggedInUserId != snapshot.val().user.id)
        //           profileList.push(this.mapProfileDetailsToList(snapshot.val(), followingIdList));
        //       }
        //     }).catch(error => {
        //       debugger;
        //     });
        // });

        return resolve(profileList);
      })
    });
  }

  mapProfileDetailsToList(element, followingIdList) {
    let profile = new ProfileListModel();

    profile.about = element.user.about;
    profile.id = element.user.id;
    profile.image = element.user.image;
    profile.name = element.user.name;
    if (element.following)
      profile.followers = Object.values(element.following).length;
    else
      profile.followers = 0;

    if (followingIdList.indexOf(element.user.id) > -1)
      profile.active = true;
    else
      profile.active = false;

    return profile;
  }

  updateData(profile): Promise<any> {

    return new Promise((resolve, reject) => {
      firebase.database().ref('/users').child(profile.user.id).update({
        user: profile.user,
        locationDetails: profile.locationDetails
      }).then(response => {
        return resolve();
      }).catch(error => {
        this.handleError(error);
      });
    });
  }

  updateUserModel(user): Promise<any> {

    return new Promise((resolve, reject) => {
      firebase.database().ref('/users/' + user.id).child('user').update(user).then(response => {
        return resolve();
      }).catch(error => {
        this.handleError(error);
      });
    });
  }

  /**
   * 
   * @param uid User id of sales representstive
   * @returns if the user has an entry in the sales rep master tabler or not
   * If the entry is there but not vaild it sets the entry to be valid
   */
  isInSalesRepMaster(uid: string): Promise<boolean> {

    return new Promise<boolean>((resolve, reject) => {

      firebase.database().ref('/salesRepMaster/' + uid).once('value', snapshot => {

        if (snapshot && snapshot.val()) {

          let result = snapshot.val() as SalesRepMaster;
          result.valid = true;

          firebase.database().ref('/salesRepMaster/' + uid).update(result);

          return resolve(true);
        }

        return resolve(false);
      });
    });
  }

  addToSalesRepMaster(user: UserModel): Promise<any> {

    let idCount;
    try {
      return new Promise<any>((resolve, reject) => {

        firebase.database().ref('/salesRepMaster/idCount').once('value', snapshot => {

          idCount = snapshot.val();
          idCount++;
          firebase.database().ref('/salesRepMaster/idCount').set(idCount);

          var salesRepMaster = new SalesRepMaster(user, idCount);

          firebase.database().ref('/salesRepMaster/').child(user.id).set(salesRepMaster);

        });
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  getFromSalesRepMaster(uid: string): Promise<SalesRepMaster> {

    return new Promise<SalesRepMaster>((resolve, reject) => {

      firebase.database().ref('/salesRepMaster/').child(uid).once('value', snapshot => {

        if (snapshot && snapshot.val()) {
          return resolve(snapshot.val());
        }
      }).catch(error => {
        this.handleError(error);
      });
    });
  }

  invalidateSaleRep(uid): Promise<any> {

    return new Promise<any>((resolve, reject) => {

      firebase.database().ref('/salesRepMaster/' + uid).once('value', snapshot => {

        if (snapshot) {

          let result = snapshot.val() as SalesRepMaster;

          result.valid = false;

          firebase.database().ref('/salesRepMaster/' + uid).update(result);
        }
      });
    });
  }

  updateLocationData(profile): Promise<any> {
    return this.locationServices.updateLocationData(profile);
  }

  checkUserAddressConflict() {

    return new Promise((resolve, reject) => {
      firebase.database().ref('/users').child('userPageAddress').once('value', snapshot => {

      }, error => {
        this.handleError(error)
      });
    });
  }

  updateLanguage(user) {
    firebase.database().ref('/users')
      .child(user.id)
      .child('user')
      .update({
        language: user.language
      });
  }

  updateCart(uid: string, cart: CartModel) {

    return new Promise((resolve, reject) => {
      firebase.database().ref('/users/' + uid).child('cart').set(cart).then(response => {
        return resolve();
      }).catch(error => {
        this.handleError(error);
      });
    });
  }

  getLast5Transactions(uid): Promise<Array<Object>> {

    return new Promise((resolve, reject) => {

      firebase.database().ref('/users/' + uid)
        .child('transactionHistory')
        .orderByChild('paymentSource')
        .equalTo('qr')
        .limitToLast(5)
        .on('value', snapshot => {

          if (snapshot) {
            if (snapshot.val()) {
              var arr = Object.values(snapshot.val()) as Array<Object>;
              return resolve(arr.reverse());
            }
            else return reject();
          } else {
            return reject();
          }
        })
    });
  }

  getUserTransactionHistory(uid): Promise<Array<Object>> {

    return new Promise((resolve, reject) => {

      firebase.database().ref('/users/' + uid)
        .child('transactionHistory')
        .orderByChild('timestamp')
        .on('value', snapshot => {

          if (snapshot) {
            if (snapshot.val())
              return resolve(Object.values(snapshot.val()) as Array<Object>);
            else return reject();
          } else {
            return reject();
          }
        })
    });
  }

  getUserTransactionDetails(tid): Promise<Array<Object>> {
    var uid = firebase.auth().currentUser.uid;

    return new Promise((resolve, reject) => {

      firebase.database().ref('/users/' + uid)
        .child('transactionHistory')
        .child(tid)
        .on('value', snapshot => {

          if (snapshot) {
            return resolve(snapshot.val());
          } else {
            return reject();
          }
        })
    });
  }

  getTransactionForTracking(uid): Promise<TransactionModel> {

    return new Promise<TransactionModel>((resolve, reject) => {

      firebase.database().ref('/users/' + uid + '/transactionHistory')
        .orderByChild('timestamp')
        .limitToLast(1)
        .once('value', snapshot => {

          if (snapshot) {
            if (snapshot.val()) {
              return resolve(Object.values(snapshot.val())[0] as TransactionModel);
            }
          }

          return reject();
        }).catch(error => this.handleError(error));
    })
  }

  async sendCustomerLocation(lat, lng, cid): Promise<any> {

    let data = {
      lat: lat,
      lng: lng,
      cid: cid
    };

    let token;

    await firebase.auth().currentUser.getIdToken(true).then(idToken => {
      token = idToken;
    }).catch(error => {
      return Promise.reject(error);
    });

    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': token
      })
    };

    return new Promise((resolve, reject) => {

      this.http.post((this.backendURL + 'customerLocation'), data, httpOptions)
        .subscribe((res) => {

          if (res['result'] == 'success')
            return resolve(res['data']);

          return reject();
        }, error => {
          return reject(error);
        });
    });
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  sendErrorToDB(error) {
    firebase.database().ref('/errors').push(error);
  }
}


// WEBPACK FOOTER //
// ./src/pages/profile/profile.service.ts